import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles, Article } from '@/mocks/articles';
import { getRecentArticles } from '@/mocks/articles';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [related, setRelated] = useState<Article[]>([]);
  const [sidebarArticles, setSidebarArticles] = useState<Article[]>([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const found = getArticleBySlug(slug);
    if (!found) {
      setNotFound(true);
      return;
    }
    setArticle(found);
    setRelated(getRelatedArticles(found, 3));
    setSidebarArticles(getRecentArticles(5).filter((a) => a.slug !== found.slug).slice(0, 4));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '火影黄漫导航',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://huoying-doujin.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SeoHead
        title={article.title}
        description={article.excerpt}
        keywords={`火影黄漫,火影忍者同人漫画,${article.tags.join(',')},${article.categoryName}`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mt-6 flex flex-col lg:flex-row gap-10">
            <article className="flex-1 min-w-0">
              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="bg-primary-100 text-primary-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-background-100 text-foreground-600 text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span>|</span>
                <span>
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
                <span>|</span>
                <span>
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
                <span>|</span>
                <span>
                  <i className="ri-eye-line mr-1"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>

              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground-500">分享：</span>
                    {['twitter', 'facebook', 'weibo'].map((platform) => (
                      <a
                        key={platform}
                        href="#"
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors"
                        rel="nofollow"
                        aria-label={`分享到${platform}`}
                      >
                        <i className={`ri-${platform === 'weibo' ? 'weibo' : platform}-line text-sm`}></i>
                      </a>
                    ))}
                  </div>
                  <Link
                    to={`/category/${article.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    ← 返回{article.categoryName}
                  </Link>
                </div>
              </div>
            </article>

            <aside className="lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <h4 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i>
                    相关推荐
                  </h4>
                  <div className="space-y-4">
                    {related.map((rel) => (
                      <Link
                        key={rel.slug}
                        to={`/article/${rel.slug}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="min-w-0">
                          <h5 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {rel.title}
                          </h5>
                          <span className="text-xs text-foreground-500 mt-1 block">
                            {rel.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h4 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-time-line text-primary-500"></i>
                    最新更新
                  </h4>
                  <div className="space-y-4">
                    {sidebarArticles.map((sa) => (
                      <Link
                        key={sa.slug}
                        to={`/article/${sa.slug}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <img
                          src={sa.image}
                          alt={sa.title}
                          className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="min-w-0">
                          <h5 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {sa.title}
                          </h5>
                          <span className="text-xs text-foreground-500 mt-1 block">
                            {sa.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h4 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-primary-500"></i>
                    热门标签
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['Pixiv', '鸣佐CP', '同人文化', '绘师推荐', '创作技巧', 'Comiket', 'DLsite', '卡卡西'].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="bg-background-200 text-foreground-600 text-xs px-2.5 py-1 rounded-full cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}